import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import Logo from "../components/Logo";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { GoogleLogo, User, Wrench } from "@phosphor-icons/react";

export default function Register() {
  const { register, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const initialRole = sp.get("role") === "tradesperson" ? "tradesperson" : "customer";
  const [role, setRole] = useState(initialRole);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      const payload = { name, email, password, role };
      const u = await register(payload);
      toast.success("Welcome to MyHandyHub!");
      if (u.role === "tradesperson") navigate("/onboarding/trader", { replace: true });
      else navigate("/dashboard/customer", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + `/auth/callback?role=${role}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const RoleBtn = ({ value, label, icon: Icon, desc }) => (
    <button
      type="button"
      onClick={() => setRole(value)}
      data-testid={`role-${value}-btn`}
      className={`flex-1 text-left p-4 rounded-md border transition-all ${
        role === value
          ? "border-[var(--brand-green)] bg-[rgba(17,82,59,0.06)]"
          : "border-[var(--border-stone)] bg-white hover:border-[var(--brand-green)]"
      }`}
    >
      <Icon size={22} weight="duotone" className="text-[var(--brand-orange)]" />
      <div className="font-display font-semibold mt-1 text-[var(--brand-green)]">{label}</div>
      <div className="text-xs text-[var(--text-muted)]">{desc}</div>
    </button>
  );

  return (
    <Layout>
      <div className="max-w-md mx-auto px-5 py-12">
        <div className="flex justify-center mb-6"><Logo size={100} /></div>
        <div className="brand-card p-7">
          <h1 className="font-display font-extrabold text-2xl text-center" style={{ color: "var(--brand-green)" }}>Create your account</h1>
          <p className="text-center text-sm text-[var(--text-muted)] mt-1">It only takes a minute.</p>

          <div className="flex gap-3 mt-5">
            <RoleBtn value="customer" label="I'm a customer" icon={User} desc="Post jobs free, forever" />
            <RoleBtn value="tradesperson" label="I'm a tradesperson" icon={Wrench} desc="£3.99/month after signup" />
          </div>

          <button
            type="button"
            onClick={handleGoogle}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-md border border-[var(--border-stone)] bg-white hover:bg-[#FAF8F2] transition-colors font-semibold"
            data-testid="register-google-btn"
          >
            <GoogleLogo size={18} weight="bold" /> Continue with Google
          </button>

          <div className="my-5 flex items-center gap-2 text-xs text-[var(--text-muted)]">
            <div className="flex-1 h-px bg-[var(--border-stone)]" /> or <div className="flex-1 h-px bg-[var(--border-stone)]" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <label className="block">
              <span className="text-xs font-semibold text-[var(--text-muted)]">Full name</span>
              <input
                data-testid="register-name-input"
                type="text" required value={name} onChange={(e) => setName(e.target.value)}
                className="w-full mt-1 px-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--brand-orange)]"
                placeholder={role === "tradesperson" ? "Tom Smith / Tom's Plumbing" : "Your name"}
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-[var(--text-muted)]">Email</span>
              <input
                data-testid="register-email-input"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-1 px-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--brand-orange)]"
                placeholder="you@example.com"
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-[var(--text-muted)]">Password</span>
              <input
                data-testid="register-password-input"
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full mt-1 px-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--brand-orange)]"
                placeholder="Min 6 characters"
              />
            </label>
            <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="register-submit-btn">
              {busy ? "Creating account…" : "Create account"}
            </button>
          </form>

          <p className="text-xs text-[var(--text-muted)] text-center mt-4">
            By signing up you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.
          </p>
          <p className="text-center text-sm mt-3">
            Already have an account? <Link to="/login" className="text-[var(--brand-green)] underline" data-testid="register-login-link">Log in</Link>
          </p>
        </div>
      </div>
    </Layout>
  );
}
